import { useState, useEffect, useCallback } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { api } from "../../services/apiService";
import { REPLAY_MASK } from "../../utils/replay-mask";

const NOTE_MAX = 500;

/**
 * Review-mode "Tag your tutor" dialog (design §10.4, slice T3). Lets a student
 * with ≥1 ACTIVE mentorship point a linked tutor at the current question, with
 * an optional note. The server enforces the whole §10.3 surface; this dialog
 * only makes honest requests and renders honest errors:
 *
 * - Tutor list = the student's ACTIVE mentorships, fetched lazily on first
 *   open (never on quiz load) and cached for the dialog's lifetime.
 * - The note is plain text, capped at {@link NOTE_MAX} chars to match the
 *   server cap. It is only ever rendered through JSX text nodes (React
 *   escapes) — never dangerouslySetInnerHTML (§10.3 XSS discipline).
 * - Error UX is uniform for the oracle-free server responses: 403/404 (no
 *   live link / unknown question) collapse into ONE generic message so the UI
 *   leaks nothing the API deliberately hides. 409 (already tagged) and 429
 *   (daily rate) are actionable self-information and get specific text.
 */
export default function TagTutorDialog({ open, onClose, questionId }) {
  const [tutors, setTutors] = useState(null); // null = not yet loaded
  const [selectedTutorId, setSelectedTutorId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const loadTutors = useCallback(async () => {
    try {
      const res = await api.listMentorships({ status: "ACTIVE" });
      const items = Array.isArray(res) ? res : [];
      // Student side: the counterpart of an ACTIVE link is the tutor.
      const active = items
        .filter((m) => m?.status === "ACTIVE" && m?.tutorUserId)
        .map((m) => ({
          tutorUserId: m.tutorUserId,
          name: m.counterpartDisplayName || m.counterpartEmail || "Your tutor",
        }));
      setTutors(active);
      if (active.length === 1) setSelectedTutorId(active[0].tutorUserId);
    } catch (e) {
      setTutors([]);
    }
  }, []);

  useEffect(() => {
    if (open && tutors === null) loadTutors();
    if (!open) {
      // fresh submit state per question visit; keep the tutor cache
      setError("");
      setDone(false);
      setNote("");
      setBusy(false);
    }
  }, [open, tutors, loadTutors]);

  const submit = async () => {
    if (!selectedTutorId || !questionId || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.createQuestionTag({
        tutorUserId: selectedTutorId,
        questionId,
        note: note.trim() ? note.trim() : undefined,
      });
      const status = res?.errStatus || res?.status;
      if (res?.id && !res?.errStatus) {
        setDone(true);
      } else if (status === 409) {
        setError("You already have a live tag on this question for this tutor.");
      } else if (status === 429) {
        setError("You've reached today's tag limit for this tutor — try again tomorrow.");
      } else {
        // Uniform for 403/404/anything else: mirrors the server's oracle-free
        // responses — the UI must not distinguish "no link" from "unknown id".
        setError("Couldn't tag your tutor for this question. Please try again.");
      }
    } catch (e) {
      setError("Couldn't tag your tutor for this question. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const noTutors = Array.isArray(tutors) && tutors.length === 0;

  return (
    // Masked (M3): the dialog lists the student's tutors by name, and the
    // chosen name shows in the select outside the masked input. The dialog
    // and the select's option list each render in their own portal.
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={REPLAY_MASK}>
      <DialogTitle>Tag your tutor</DialogTitle>
      <DialogContent>
        {done ? (
          <Typography variant="body2" data-testid="tag-done">
            Tagged — your tutor will see this question.
          </Typography>
        ) : tutors === null ? (
          <CircularProgress size={22} data-testid="tag-loading" />
        ) : noTutors ? (
          <Typography variant="body2" data-testid="tag-no-tutors">
            You don't have a tutor connected yet. Connect with a tutor under
            My&nbsp;Tutors first.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" className="tag-sub">
              Flag this question for one of your tutors to review with you.
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              margin="dense"
              label="Tutor"
              value={selectedTutorId}
              onChange={(e) => setSelectedTutorId(e.target.value)}
              inputProps={{ "data-testid": "tag-tutor-select" }}
              SelectProps={{ MenuProps: { PaperProps: REPLAY_MASK } }}
            >
              {tutors.map((t) => (
                <MenuItem key={t.tutorUserId} value={t.tutorUserId}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              margin="dense"
              multiline
              maxRows={4}
              label={`Note (optional, ${NOTE_MAX} chars max)`}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
              inputProps={{ maxLength: NOTE_MAX, "data-testid": "tag-note" }}
            />
            {error && (
              <Typography variant="body2" color="error" data-testid="tag-error">
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="tag-close">
          {done ? "Done" : "Cancel"}
        </Button>
        {!done && !noTutors && tutors !== null && (
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedTutorId || busy}
            onClick={submit}
            data-testid="tag-submit"
          >
            {busy ? <CircularProgress size={20} color="inherit" /> : "Tag tutor"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
